import { computed, inject, Type } from '@angular/core';

import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { OpenDialogState, withDialogFeatures, withSnackBarFeature } from "@data-access/shared";

import { DEFAULT_SUBMISSION_STATE } from '../../constants';

import {
  IrisMapping,
  IrisSettingsDialogData,
  SubmissionSignalState,
} from '../../types';

import { SubmissionService } from '../../services';

export const SUBMISSION_SIGNAL_STORE = signalStore(
  withState(() => inject(DEFAULT_SUBMISSION_STATE)),
  withProps(({ ...store }) => ({
    _submissionService: inject(SubmissionService),
    store: store,
  })),
  withFeature(() => withDialogFeatures()),
  withFeature(() => withSnackBarFeature()),
  withComputed(
    ({ iris_mapping, isLoading, canCloseDialog, irisRoutingGroupDta }) => ({
      $canCloseDialog: computed(() => canCloseDialog()),
      $irisMapping: computed(() => iris_mapping()),
      $irisRoutingGroups: computed(() => irisRoutingGroupDta.data()),
      $isLoading: computed(() => isLoading()),
    }),
  ),
  withMethods(({ _submissionService, openDialog, ...store }) => {
    const _openDialog = (data: OpenDialogState<IrisSettingsDialogData>) => {
      patchState(store, { canCloseDialog: false });
      openDialog(data);
    };

    const _getSubmissionTypeIrisMapping = rxMethod<{
      workspaceTypeId: string;
      entity_type: string;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ workspaceTypeId, entity_type }) =>
          _submissionService
            .getSubmissionTypeIrisMapping(workspaceTypeId, entity_type)
            .pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error: error }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (irisMapping) =>
                  patchState(store, {
                    entity_type: entity_type,
                    iris_mapping: { ...irisMapping },
                    workspaceTypeId: workspaceTypeId,
                  }),
              }),
            ),
        ),
      ),
    );

    const _getIrisRoutingGroups = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          _submissionService.getIrisRoutingGroups().pipe(
            tapResponse({
              error: (error: Error) => patchState(store, { error: error }),
              finalize: () => patchState(store, { isLoading: false }),
              next: (routingGroups) =>
                patchState(store, { irisRoutingGroupDta: routingGroups }),
            }),
          ),
        ),
      ),
    );

    const _updateSubmissionTypeIrisMapping = rxMethod<{
      irisMapping: IrisMapping;
    }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ irisMapping }) =>
          _submissionService
            .updateSubmissionTypeIrisMapping(
              store.workspaceTypeId(),
              store.entity_type(),
              irisMapping,
            )
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: () => {
                  patchState(store, {
                    canCloseDialog: true,
                    iris_mapping: { ...irisMapping },
                  });
                  store.displaySnackBar('IRIS settings has updated successfully.');
                }
              }),
            ),
        ),
      ),
    );

    return {
      getIrisRoutingGroups: () => _getIrisRoutingGroups(),
      getSubmissionTypeIrisMapping: (
        workspaceTypeId: string,
        entity_type: string,
      ) =>
        _getSubmissionTypeIrisMapping({
          entity_type,
          workspaceTypeId,
        }),
      openDialog: (data: OpenDialogState<IrisSettingsDialogData>) =>
        _openDialog(data),
      updateSubmissionTypeIrisMapping: (irisMapping: IrisMapping) =>
        _updateSubmissionTypeIrisMapping({
          irisMapping,
        }),
    };
  }),
) satisfies Type<SubmissionSignalState>;
